import { useCallback, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import moment from "moment-timezone";
import {
  SEND_CHECK_AVAILABILITY,
  UPDATE_BOOKING,
} from "@store/operations/bookingOperations";
import { SEND_LEAD_MESSAGE } from "@store/operations/messageOperations";
import {
  GET_DASHBOARD_BOOKING_DETAIL,
  GET_DASHBOARD_CONVERSATION,
} from "@store/operations/bookingDashboardOperations";
import {
  CHECK_DASHBOARD_AVAILABILITY,
  COMPLETE_DASHBOARD_TASK,
  GET_DASHBOARD_TASK_STATES,
  MARK_DASHBOARD_MESSAGE_AS_SEEN,
  SUGGEST_DASHBOARD_REPLY,
} from "@store/operations/bookingDashboardActionOperations";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MESSAGE_TASK_TYPES = new Set([
  "newEnquiryFollowup", "newBookingMissingDetails", "optionsSentNotSeen",
  "optionsSeenNoQuote", "quoteSentNotSeen", "quoteSeenNoPayment", "quoteExpired",
]);
const FAILURE_MESSAGES = {
  "save-notes": "Your notes could not be saved. Keep your changes and try again.",
  "complete-task": "The task could not be completed. Please try again.",
  "send-message": "The message could not be confirmed as sent. Check the conversation before sending again.",
  "set-booking-availability": "The booking availability could not be updated. Please try again.",
  "check-availability": "Availability could not be checked. Please try again.",
  "send-availability-request": "The request could not be confirmed for every operator. Check the conversation before sending again.",
  "mark-messages-read": "Some messages could not be marked as read. Please try again.",
  "suggest-reply": "A reply could not be suggested. Your message is still here.",
};

class DashboardActionError extends Error {}

function requireValue(condition, message) {
  if (!condition) throw new DashboardActionError(message);
}

function isUUID(value) {
  return typeof value === "string" && UUID.test(value);
}

function requireText(value, message, allowEmpty = false) {
  requireValue(typeof value === "string" && (allowEmpty || value.trim().length > 0), message);
  return value;
}

function knownBooking(payload, id) {
  const booking = isUUID(id) && payload?.bookings?.find((entry) => entry.id === id);
  requireValue(Boolean(booking), "This booking is no longer available. Refresh the page and try again.");
  return booking;
}

function bookingBoatIds(booking) {
  const ids = [];
  const seen = new Set();
  (booking.items || []).forEach((item) => {
    if (item.boat?.id && !seen.has(item.boat.id)) {
      seen.add(item.boat.id);
      ids.push(item.boat.id);
    }
  });
  return ids;
}

function checkAllBoats(booking, requestedIDs) {
  const boats = bookingBoatIds(booking);
  requireValue(boats.length > 0, "Add a boat before continuing.");
  if (requestedIDs === undefined) return;
  requireValue(Array.isArray(requestedIDs) && requestedIDs.every(isUUID), "Choose the booking boats and try again.");
  const expected = new Set(boats);
  const requested = new Set(requestedIDs);
  requireValue(requested.size === expected.size && [...expected].every((id) => requested.has(id)), "This action covers every boat in the booking. Select all boats to continue.");
}

async function completeSentMessageTask(client, mutate, bookingId, taskId, matchedAtSend) {
  if (taskId == null) return { status: "not-requested" };
  const changed = {
    status: "changed",
    taskId,
    message: "Message sent, but the task has changed. Check the booking's current task.",
  };
  if (!isUUID(taskId) || !matchedAtSend) return changed;

  try {
    const response = await client.query({
      query: GET_DASHBOARD_TASK_STATES,
      fetchPolicy: "network-only",
      errorPolicy: "none",
    });
    const tasks = response.data?.task;
    requireValue(Array.isArray(tasks), "Task completion could not be confirmed.");
    // Only the explicit task booking association establishes ownership. A lead
    // message linked to this booking does not promote its enquiry task.
    const bookingTasks = tasks.filter((task) => task.booking?.id === bookingId);
    const captured = bookingTasks.find((task) => task.id === taskId);
    if (!captured || !MESSAGE_TASK_TYPES.has(captured.type)) return changed;
    if (captured.status === "done") return { status: "already-completed", taskId };

    const latest = bookingTasks.reduce((current, candidate) => {
      const candidateTime = candidate.createdAt == null ? NaN : moment(candidate.createdAt, moment.ISO_8601, true).valueOf();
      requireValue(isUUID(candidate.id) && Number.isFinite(candidateTime) && ["pending", "done"].includes(candidate.status), "Task completion could not be confirmed.");
      if (!current) return { task: candidate, time: candidateTime };
      return candidateTime > current.time || (candidateTime === current.time && candidate.id.localeCompare(current.task.id) > 0)
        ? { task: candidate, time: candidateTime } : current;
    }, null)?.task;
    if (latest?.id !== taskId || captured.status !== "pending") return changed;

    // Use the captured ID even if another task appears after this read. Never
    // substitute a newer task for the one this message was written to resolve.
    const completed = await mutate(COMPLETE_DASHBOARD_TASK, { id: taskId });
    const saved = completed.data?.markAsDone;
    requireValue(saved?.id === taskId && saved.status === "done", "Task completion could not be confirmed.");
    return { status: "completed", taskId };
  } catch (error) {
    console.error("Message sent but its task completion could not be confirmed.");
    return {
      status: "failed",
      taskId,
      message: "Message sent, but task completion could not be confirmed. Check the booking's current task.",
    };
  }
}

export default function useBookingDashboardActions({ getPayload, onChanged }) {
  const client = useApolloClient();
  const pending = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      if (typeof onChanged !== "function") return false;
      return (await onChanged()) !== false;
    } catch (error) {
      console.error("Booking dashboard could not refresh after an action.");
      return false;
    }
  }, [onChanged]);

  const executeAction = useCallback(async (request) => {
    const action = request?.action;
    requireValue(Object.hasOwn(FAILURE_MESSAGES, action), "This action is unavailable.");
    const payload = typeof getPayload === "function" ? getPayload() : null;
    const booking = knownBooking(payload, request.bookingId);
    const input = request.input && typeof request.input === "object" && !Array.isArray(request.input) ? request.input : {};
    const key = `${action}:${action === "complete-task" ? request.taskId : booking.id}`;
    requireValue(!pending.current.has(key), "This action is already in progress.");
    pending.current.add(key);

    const mutate = (mutation, variables) => client.mutate({
      mutation,
      variables,
      errorPolicy: "none",
      fetchPolicy: "no-cache",
    });

    try {
      let result;
      let changed = true;
      if (action === "save-notes" || action === "set-booking-availability") {
        const values = action === "save-notes"
          ? { notes: requireText(input.notes, "Enter your notes before saving.", true) }
          : { available: input.available };
        if (action === "set-booking-availability") requireValue(typeof input.available === "boolean", "Choose whether the booking is available.");
        // The server applies only supplied fields. Omit all unrelated fields,
        // including source and tracking, to preserve their current stored values.
        const response = await mutate(UPDATE_BOOKING, { id: booking.id, input: values });
        const saved = response.data?.updateBooking;
        requireValue(saved?.id === booking.id, FAILURE_MESSAGES[action]);
        requireValue(action === "save-notes" ? saved.notes === values.notes : saved.available === values.available, FAILURE_MESSAGES[action]);
        result = action === "save-notes" ? { bookingId: saved.id, notes: saved.notes } : { bookingId: saved.id, available: saved.available };
      } else if (action === "complete-task") {
        const task = payload?.tasksByBooking?.get(booking.id);
        requireValue(isUUID(request.taskId) && task?.id === request.taskId && task.status === "pending", "This task has changed. Refresh the page before completing it.");
        const response = await mutate(COMPLETE_DASHBOARD_TASK, { id: request.taskId });
        const saved = response.data?.markAsDone;
        requireValue(saved?.id === request.taskId && saved.status === "done", FAILURE_MESSAGES[action]);
        result = { bookingId: booking.id, task: saved };
      } else if (action === "send-message") {
        const taskAtSend = payload?.tasksByBooking?.get(booking.id);
        const capturedTaskAtSend = Boolean(taskAtSend && taskAtSend.id === request.taskId);
        const taskForCompletion = capturedTaskAtSend && !MESSAGE_TASK_TYPES.has(taskAtSend.type) ? null : request.taskId;
        const matchedTaskAtSend = capturedTaskAtSend && MESSAGE_TASK_TYPES.has(taskAtSend.type)
          && ["pending", "done"].includes(taskAtSend.status);
        const content = requireText(input.content, "Write a message before sending it.");
        const leadId = request.leadId || booking.lead?.id;
        requireValue(isUUID(leadId) && leadId === booking.lead?.id, "The recipient has changed. Refresh the booking and try again.");
        requireValue(typeof booking.lead?.email === "string" && booking.lead.email.trim().length > 0, "Add an email address before sending a message.");
        requireValue(typeof input.sendSms === "boolean", "Choose whether to send a text notification.");
        if (input.sendSms) requireValue(typeof booking.lead?.phone === "string" && booking.lead.phone.trim().length > 0, "Add a phone number before sending a text notification.");
        const values = { content, booking: { id: booking.id }, sendSms: input.sendSms };
        if (input.quoteId) {
          requireValue(isUUID(input.quoteId) && booking.quotes?.some((quote) => quote.id === input.quoteId), "This quote is no longer available.");
          values.quote = { id: input.quoteId };
        }
        if (input.optionId) {
          requireValue(isUUID(input.optionId) && booking.options?.some((option) => option.id === input.optionId), "These options are no longer available.");
          values.bookingOption = { id: input.optionId };
        }
        const response = await mutate(SEND_LEAD_MESSAGE, { input: values, toLeadID: leadId });
        const sent = response.data?.sendAndCreateLeadMessage;
        if (!sent?.id || sent.status !== "sent") {
          await refresh();
          throw new DashboardActionError("The message was not sent. Your text has been kept. Please try again.");
        }
        const taskCompletion = await completeSentMessageTask(client, mutate, booking.id, taskForCompletion, matchedTaskAtSend);
        result = { bookingId: booking.id, message: sent, taskCompletion };
      } else if (action === "check-availability" || action === "send-availability-request") {
        checkAllBoats(booking, input.boatIds);
        if (action === "check-availability") {
          requireValue(Boolean(booking.cruiseDate) && Number.isFinite(booking.hours) && booking.hours > 0, "Add a cruise date and duration before checking availability.");
          const response = await mutate(CHECK_DASHBOARD_AVAILABILITY, { id: booking.id });
          const checks = response.data?.checkAvailability;
          const boats = bookingBoatIds(booking);
          requireValue(Array.isArray(checks) && checks.length > 0 && boats.every((id) => checks.some((check) => check.boatId === id)), FAILURE_MESSAGES[action]);
          result = { bookingId: booking.id, checks, checkedAt: new Date().toISOString() };
          changed = false;
        } else {
          const message = requireText(input.message, "Write a request before sending it.");
          const detail = await client.query({ query: GET_DASHBOARD_BOOKING_DETAIL, variables: { id: booking.id }, fetchPolicy: "network-only", errorPolicy: "none" });
          const items = detail.data?.booking?.items;
          requireValue(Array.isArray(items) && items.length > 0 && items.every((item) => item.boat?.owner?.email?.trim()), "Add an operator email for every boat before sending this request.");
          const response = await mutate(SEND_CHECK_AVAILABILITY, { id: booking.id, message });
          const saved = response.data?.sendAvailabilityCheck;
          requireValue(saved?.id === booking.id && Boolean(saved.availabilityCheckSentOn), FAILURE_MESSAGES[action]);
          result = { bookingId: saved.id, availabilityCheckSentOn: saved.availabilityCheckSentOn };
        }
      } else if (action === "mark-messages-read") {
        const leadId = request.leadId || booking.lead?.id;
        requireValue(isUUID(leadId) && leadId === booking.lead?.id, "The conversation has changed. Open it again and try again.");
        requireValue(Array.isArray(input.messageIds) && input.messageIds.length > 0 && input.messageIds.every(isUUID), "Choose received messages to mark as read.");
        const ids = [...new Set(input.messageIds)];
        const response = await client.query({ query: GET_DASHBOARD_CONVERSATION, variables: { leadID: leadId }, fetchPolicy: "network-only", errorPolicy: "none" });
        const messages = response.data?.messagesByLead;
        requireValue(Array.isArray(messages), FAILURE_MESSAGES[action]);
        const selected = ids.map((id) => messages.find((message) => message.id === id));
        requireValue(selected.every((message) => message && (message.lead || message.owner)), "These received messages are no longer available.");
        const outcomes = await Promise.allSettled(selected.map((message) => message.seenOn
          ? Promise.resolve(message)
          : mutate(MARK_DASHBOARD_MESSAGE_AS_SEEN, { id: message.id }).then((value) => {
            const marked = value.data?.markAsSeen;
            requireValue(marked?.id === message.id && Boolean(marked.seenOn), FAILURE_MESSAGES[action]);
            return marked;
          })));
        const marked = outcomes.filter((outcome) => outcome.status === "fulfilled" && outcome.value?.seenOn).map((outcome) => ({ id: outcome.value.id, seenOn: outcome.value.seenOn }));
        if (marked.length !== ids.length) {
          await refresh();
          throw new DashboardActionError(FAILURE_MESSAGES[action]);
        }
        result = { bookingId: booking.id, messages: marked };
      } else if (action === "suggest-reply") {
        requireValue(isUUID(request.requestId), "Open the message and try suggesting a reply again.");
        if (input.draft !== undefined) requireText(input.draft, "Your message could not be read. Open it again and try again.", true);
        const response = await mutate(SUGGEST_DASHBOARD_REPLY, { bookingID: booking.id, requestID: request.requestId, draft: input.draft });
        const reply = response.data?.suggestBookingReply;
        requireValue(typeof reply === "string" && reply.trim().length > 0, FAILURE_MESSAGES[action]);
        result = { bookingId: booking.id, reply };
        changed = false;
      }
      const refreshed = changed ? await refresh() : true;
      return { ...result, refreshRequired: !refreshed };
    } catch (error) {
      if (error instanceof DashboardActionError) throw error;
      if (action === "send-message" || action === "send-availability-request") await refresh();
      throw new DashboardActionError(FAILURE_MESSAGES[action]);
    } finally {
      pending.current.delete(key);
    }
  }, [client, getPayload, refresh]);

  return { executeAction };
}
